import React, { useRef, createRef, useEffect, useState, memo } from 'react'
import PropTypes from 'prop-types'
import { useRecoilValue } from 'recoil'
import { useAnimation } from 'framer-motion'
import {
  siteData as siteDataState,
  servicesData as servicesDataState,
} from '../../store/dataStore'
import CustomScroller from 'react-custom-scroller'

import {
  ServicesSection,
  ServicesWrapper,
  ServicesTitle,
  ServicesDescription,
  ServicesLinks,
} from '../../styles/servicesStyles'
import ServiceLink from './ServiceLink'

const Services = ({ visible }) => {
  const servicesData = useRecoilValue(servicesDataState)
  const services = servicesData.services
  const siteData = useRecoilValue(siteDataState)

  const servicesSection = useRef(null)
  const servicesTitle = useRef(null)
  const servicesDesc = useRef(null)
  const servicesLinks = useRef(services.map(() => createRef()))
  const servicesScroller = useRef(null)

  const servicesSectionVars = {
    hidden: {
      visibility: 'hidden',
      zIndex: -1,
      opacity: 0,
    },
    visible: {
      visibility: 'visible',
      zIndex: 1,
      opacity: 1,
    },
    exit: {
      opacity: 0,
      transition: {
        duration: 1,
        delay: 0.5,
      },
      transitionEnd: {
        visibility: 'hidden',
        zIndex: -1,
      },
    },
  }

  const servicesTitleVars = {
    hidden: {
      opacity: 0,
      y: '150%',
      transition: {
        duration: 1,
        delay: 0.5,
        ease: 'easeOut',
      },
    },
    visible: {
      opacity: 1,
      y: 0,
    },
  }

  const servicesDescVars = {
    hidden: {
      opacity: 0,
      y: '125%',
      transition: {
        duration: 2,
        delay: 0.25,
        ease: 'easeOut',
      },
    },
    visible: {
      opacity: 1,
      y: 0,
    },
  }

  const servicesAnims = useAnimation(servicesSectionVars)
  const servicesTitleAnims = useAnimation(servicesTitleVars)
  const servicesDescAnims = useAnimation(servicesDescVars)

  const [readMore, setReadMore] = useState(false)
  const [label, setLabel] = useState('')

  const handleReadMore = () => {
    setReadMore(!readMore)
  }

  useEffect(() => {
    if (visible) {
      servicesAnims.start('visible')
      servicesTitleAnims.start('visible')
      servicesDescAnims.start('visible')
      servicesLinks.current.map((ref) => ref.current.playVisible())
      servicesScroller.current.scrollTop = 0
    } else {
      servicesTitleAnims.start('hidden')
      servicesDescAnims.start('hidden')
      servicesLinks.current.map((ref) => ref.current.playHidden())
      servicesAnims.start('exit')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible])

  useEffect(() => {
    if (readMore) {
      const collapse = siteData.language === 'es' ? 'Cerrar' : 'Close'
      setLabel(collapse)
    } else {
      const more = siteData.language === 'es' ? 'Leer más' : 'Read more'
      setLabel(more)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readMore])

  return (
    <ServicesSection
      ref={servicesSection}
      initial='hidden'
      animate={servicesAnims}
      variants={servicesSectionVars}
      transition={{
        opacity: {
          duration: 1.5,
          ease: 'linear',
        },
        default: {
          duration: 0,
          ease: 'none',
        },
      }}>
      <CustomScroller
        ref={servicesScroller}
        className='Scroller'
        innerClassName='ScrollerContent'>
        <ServicesWrapper>
          <ServicesTitle
            ref={servicesTitle}
            initial='hidden'
            animate={servicesTitleAnims}
            variants={servicesTitleVars}
            transition={{
              duration: 1.25,
              ease: 'easeOut',
              delay: 0.75,
            }}>
            {servicesData.title}
          </ServicesTitle>
          <ServicesDescription
            ref={servicesDesc}
            className={readMore ? 'showMore' : ''}
            initial='hidden'
            animate={servicesDescAnims}
            variants={servicesDescVars}
            transition={{
              duration: 1.15,
              ease: 'easeOut',
              delay: 0.75,
            }}>
            <div
              dangerouslySetInnerHTML={{ __html: servicesData.description }}
            />
            <button className='readMoreMobile' onClick={() => handleReadMore()}>
              {label}
            </button>
          </ServicesDescription>
          <ServicesLinks>
            {services.map((service, index) => (
              <ServiceLink
                key={`service-${index}`}
                ref={servicesLinks.current[index]}
                index={index}
                title={service.title}
              />
            ))}
          </ServicesLinks>
        </ServicesWrapper>
      </CustomScroller>
    </ServicesSection>
  )
}

Services.propTypes = {
  visible: PropTypes.bool.isRequired,
}

export default memo(Services)
